import { useCallback, useEffect, useState } from 'react';
import { File as FileIcon, Folder, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { spaceErrorMessage, spacesApi, type SpaceTrashRow } from '../../lib/api';
import { cn, formatDate, timeAgo } from '../../lib/utils';
import { Button } from '../ui/button';

const LIMIT = 200;

function purgeLabel(row: SpaceTrashRow): string | null {
  if (!row.purgeAfter) return null;
  const days = Math.ceil((new Date(row.purgeAfter).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return 'deleted for good shortly';
  return `deleted for good in ${days} day${days === 1 ? '' : 's'}`;
}

/**
 * Your space's trash. Only your own: something an editor deletes in a folder you shared lands here too,
 * since it is your space and your quota, and you are the one who can bring it back.
 */
export function TrashView({
  trashDays, onRestored,
}: {
  trashDays: number;
  /** Restored into this folder: its listing and the tree's view of it are stale. */
  onRestored: (parentId: string) => void;
}) {
  const [rows, setRows] = useState<SpaceTrashRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await spacesApi.trash({ limit: LIMIT }));
      setError(null);
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not load the trash'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function restore(row: SpaceTrashRow) {
    setRestoring(row.id);
    setError(null);
    try {
      if (row.kind === 'folder') await spacesApi.restoreFolder(row.id);
      else await spacesApi.restoreFile(row.id);
      setRows((prev) => prev?.filter((r) => r.id !== row.id) ?? null);
      onRestored(row.parentId);
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not restore it'));
    } finally {
      setRestoring(null);
    }
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-ink flex items-center gap-2">
        <Trash2 size={16} className="text-ink-faint" />
        Trash
      </h1>
      <p className="mt-1 mb-5 max-w-2xl text-xs text-ink-muted">
        Everything moved to the trash in your space, including what people you shared with deleted. Items are deleted for good after{' '}
        {trashDays} days. Restoring a folder brings back everything that was in it.
      </p>

      {error && <div className="mb-4 rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>}

      {rows === null ? (
        <p className="text-xs text-ink-faint">{error ? '' : 'Loading…'}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card py-16 text-center text-ink-faint">
          <Trash2 size={20} className="mx-auto mb-2 opacity-60" />
          <p className="text-sm">The trash is empty.</p>
        </div>
      ) : (
        <>
          <ul className="rounded-lg border border-border bg-card divide-y divide-border-subtle">
            {rows.map((row) => {
              const purge = purgeLabel(row);
              return (
                <li key={`${row.kind}-${row.id}`} className="flex items-center gap-3 px-4 py-2.5">
                  {row.kind === 'folder' ? (
                    <Folder size={14} className="text-orange-500 flex-shrink-0" />
                  ) : (
                    <FileIcon size={14} className="text-ink-faint flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink truncate">{row.name}</p>
                    <p className="text-[11px] text-ink-faint truncate">
                      <span className="font-mono">{row.address}</span>
                      {' · '}
                      <span title={formatDate(row.deletedAt)}>trashed {timeAgo(row.deletedAt)}</span>
                      {purge && <span className={cn(purge.endsWith('shortly') && 'text-warning-text')}> · {purge}</span>}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => void restore(row)} disabled={restoring !== null}>
                    {restoring === row.id ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Restore
                  </Button>
                </li>
              );
            })}
          </ul>
          {rows.length >= LIMIT && <p className="mt-2 text-[11px] text-ink-faint">Showing the {LIMIT} most recent.</p>}
        </>
      )}
    </div>
  );
}
